"use client";
import type { UseInfiniteQueryResult } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import type { z, ZodType, ZodTypeAny } from "zod";

import { useDebouncedCallback as useDebounce } from "./useDebouncedCallback";

type UseInfiniteScrollProps<T extends ZodType = ZodTypeAny> = {
  query: UseInfiniteQueryResult<{
    pages: z.TypeOf<T>[];
    pageParams: number[];
  }>;
} & (
  | {
      scrollingElement?: Element;
      distanceToEnd?: number;
      screenPercentRemaining?: never;
    }
  | {
      scrollingElement?: never;
      distanceToEnd?: never;
      screenPercentRemaining: number;
    }
);

export const useInfiniteScroll = <T extends ZodType = ZodTypeAny>({
  query,
  scrollingElement,
  distanceToEnd = 500,
  screenPercentRemaining,
}: UseInfiniteScrollProps<T>) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [distance, setDistance] = useState<number>(distanceToEnd);

  useEffect(() => {
    const checkScreenSize = () => {
      if (screenPercentRemaining > 0 && typeof window !== "undefined") {
        const screenHeight = window.innerHeight;
        setDistance(screenHeight * screenPercentRemaining);
      }
    };

    if (screenPercentRemaining > 0) {
      checkScreenSize();

      if (typeof window !== "undefined") {
        window.addEventListener("resize", checkScreenSize);
      }
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("resize", checkScreenSize);
      }
    };
  }, [screenPercentRemaining]);

  const handleScroll = useDebounce(async () => {
    const scroller = scrollingElement ?? document.getElementById("scrollBody");
    if (
      scroller.scrollTop + scroller.clientHeight >
        scroller.scrollHeight - distance &&
      !query.isFetchingNextPage
    ) {
      if (query.data && query.hasNextPage) {
        setLoading(true);
        await query.fetchNextPage();
        setLoading(false);
      }
    }
  }, 50);

  useEffect(() => {
    if (!scrollingElement) {
      if (typeof document !== "undefined") {
        const scroller = document.getElementById("scrollBody");
        scroller.addEventListener("scroll", handleScroll);
      }

      return () => {
        if (typeof document !== "undefined") {
          const scroller = document.getElementById("scrollBody");
          scroller.removeEventListener("scroll", handleScroll);
        }
      };
    }
  }, [query, scrollingElement, handleScroll]);

  return { handleScroll, loading };
};
